import axios from 'axios';
import { getVisitorId } from '@/utils/visitorId';
import i18n from '@/i18n';

// Allauth headless stores the current session token under this key.
// See src/stores/authStore.js for the matching constant.
const SESSION_TOKEN_KEY = 'pfSessionToken';

// The API is a different origin to the frontend: properflight.com serves the
// SPA, v2.properflight.com is the Django backend. The production default must
// therefore be v2 - pointing it at properflight.com returns the SPA's HTML for
// every API call, which breaks the whole app with no build-time error.
// VUE_APP_API_URL is set in the DO App Platform spec; this is the safety net if
// it ever goes missing.
const BASE_URL =
  process.env.VUE_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://v2.properflight.com'
    : 'http://localhost:8000');

// Exposed for building absolute backend URLs outside of axios calls
// (e.g. the Kiwi click-out redirect gateway href).
export const API_BASE_URL = BASE_URL;

// Buddy search queries Kiwi once per traveller before it can answer, which lands
// around 30s - right on the default timeout, so results arrived or not depending on
// luck. Pass this to those calls rather than raising the global limit, which would
// leave genuinely stuck requests spinning for two minutes.
export const LONG_SEARCH_TIMEOUT = 120000;

const instance = axios.create({
  baseURL: BASE_URL,
  withCredentials: false,
  timeout: 30000,
});

// ----------------------------------------------------------------------
// Request: attach the allauth session token on every outgoing request.
// Allauth expects the header name `X-Session-Token` in app-mode.
// ----------------------------------------------------------------------
// Allauth rejects these flows with 401/410 if a live session token is
// attached, since they assume an unauthenticated caller. The user may
// still have a leftover pfSessionToken in localStorage (e.g. they reset
// their password from a tab that was previously logged in), so we strip
// the header for these endpoints explicitly.
const PRE_AUTH_ENDPOINTS = [
  '/_allauth/app/v1/auth/password/request',
  '/_allauth/app/v1/auth/password/reset',
  '/_allauth/app/v1/auth/signup',
];

instance.interceptors.request.use(
  (config) => {
    const url = config.url || '';
    const isPreAuth = PRE_AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
    if (isPreAuth) {
      if (config.headers) delete config.headers['X-Session-Token'];
      return config;
    }
    const token = localStorage.getItem(SESSION_TOKEN_KEY);
    if (token) {
      config.headers = config.headers || {};
      config.headers['X-Session-Token'] = token;
    }

    // Tell the backend which language to render city names in. It resolves
    // them server-side and returns them already translated, so no component
    // has to remember to localise a city name.
    try {
      const locale = i18n.global.locale.value;
      if (locale) {
        config.headers = config.headers || {};
        config.headers['Accept-Language'] = locale;
      }
    } catch (_) {
      // fall back to the backend default (English)
    }

    // First-party visitor ID — lets the backend attribute searches and book
    // clicks to the campaign that produced them, without relying on IP hashes
    // (which collapse everyone behind a shared NAT into a single "visitor").
    try {
      const visitorId = getVisitorId();
      if (visitorId) {
        config.headers = config.headers || {};
        config.headers['X-Visitor-Id'] = visitorId;
      }
    } catch (_) {
      // never block a request over analytics
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ----------------------------------------------------------------------
// Response: on 401/410 from any endpoint, treat the session as dead.
// We don't attempt any refresh here: allauth session tokens are long-
// lived and roll forward automatically. A hard 401 means "re-login".
// Also: report unexpected 5xx responses to the client-error endpoint.
// ----------------------------------------------------------------------

// Endpoints we never report to avoid infinite loops or noise.
const NO_REPORT_URLS = [
  '/analytics/client-error/',
  '/_allauth/app/v1/',
];
const reportedApiErrors = new Map();
const apiErrorReportTimes = [];

instance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';

    // All allauth endpoints (login/signup/session/verify/reset/...) have
    // their own 401 semantics — pending flows, bad creds, expired codes.
    // The authStore decides what to do with those; don't race it here.
    const isAllauthEndpoint = url.includes('/_allauth/app/v1/');

    if ((status === 401 || status === 410) && !isAllauthEndpoint) {
      // Defer import to avoid a circular dependency with the store.
      import('@/stores/authStore')
        .then(({ useAuthStore }) => {
          try { useAuthStore().clearSession(); } catch (_) { /* ignore */ }
        })
        .catch(() => { /* ignore */ });
    }

    // Report unexpected server errors (5xx) from our own API.
    const shouldReport =
      status >= 500 &&
      !NO_REPORT_URLS.some((u) => url.includes(u));

    if (shouldReport) {
      try {
        const now = Date.now();
        const fingerprint = `${status}:${url}`;
        const lastReportedAt = reportedApiErrors.get(fingerprint) || 0;
        while (apiErrorReportTimes.length && now - apiErrorReportTimes[0] >= 60000) {
          apiErrorReportTimes.shift();
        }
        if (now - lastReportedAt >= 60000 && apiErrorReportTimes.length < 10) {
          reportedApiErrors.set(fingerprint, now);
          apiErrorReportTimes.push(now);
          instance.post('/analytics/client-error/', {
            error_type: 'api',
            message: `HTTP ${status} from ${url}`,
            stack: error.response?.data
              ? JSON.stringify(error.response.data).slice(0, 500)
              : '',
            page_url: typeof window !== 'undefined' ? window.location.href : '',
            route_full_path: typeof window !== 'undefined' ? window.location.pathname + window.location.search : '',
            locale: typeof navigator !== 'undefined' ? (navigator.language || '') : '',
            api_endpoint: url,
            http_status: status,
          }).catch(() => {});
        }
      } catch (_) {
        // intentionally swallowed
      }
    }

    return Promise.reject(error);
  },
);

export default instance;
